import { defineComponent, ref, shallowRef, h, Component, App } from "vue";
import type { BasePopupProps, PopupConfig } from "@/shared/plugins/interface";
import PopupLayout from "@/shared/components/popup-layout/PopupLayout.vue";

/**
 * @description PopupItems
 */
interface PopupItems<T extends BasePopupProps = BasePopupProps> {
    component: Component;
    props?: T;
    key: string;
    componentInstance?: unknown;
}

/**
 * @description 팝업 키 관리
 */
const popupKey = ref<number>(0);

/**
 * @description 전역 popupItems 저장소
 */
const popupItems = shallowRef<PopupItems[]>([]);

/**
 * @description 팝업키를 설정합니다.
 * @param {string} key 팝업키
 */
const setPopupKey = (key: string) => {
    if (key !== "") {
        return key;
    }

    const newKey = `popup-${popupKey.value}`;
    popupKey.value++;

    return newKey;
};

/**
 * @description 중복된 팝업인지 체크합니다.
 */
const checkDuplicatePopup = (key: string) => {
    if (popupItems.value.length === 0) {
        return false;
    }

    return popupItems.value.some((item) => item.key === key);
};

/**
 * @description 팝업 리스트에 팝업 추가
 * @param config 팝업 설정 (컴포넌트와 props)
 */
const openPopup = <T extends BasePopupProps>(config: PopupConfig<T>) => {
    // key 를 생성해줍니다.
    const componentKey = setPopupKey(config.props?.closeKey);

    if (checkDuplicatePopup(componentKey)) {
        return componentKey;
    }

    // 팝업 아이템을 생성합니다.
    const newPopupItems = [
        ...popupItems.value,
        {
            component: config.component,
            props: {
                ...config.props,
                closeKey: componentKey,
            },
            key: componentKey,
            componentInstance: null,
        },
    ];

    popupItems.value = newPopupItems;

    return componentKey;
};

/**
 * @description 팝업 닫기
 * @param {string} closeKey
 */
const closePopup = (closeKey: string) => {
    if (!closeKey) return;

    const newPopupItems = popupItems.value.filter((item) => item.key !== closeKey);
    popupItems.value = newPopupItems;
};

/**
 * @description 팝업 리스트 초기화
 */
const clearPopup = () => {
    popupItems.value = [];
    popupKey.value = 0;
};

/**
 * @description 팝업 리스트 조회
 */
const getPopupList = () => {
    return popupItems.value;
};

/**
 * @description 팝업 관리자 인스턴스 생성
 */
const PopupAnchor = defineComponent({
    name: "PopupAnchor",
    setup() {
        return () => {
            if (popupItems.value.length === 0) {
                return null;
            }

            const children = popupItems.value.map((popupItem, index) => {
                // const baseZIndex = 999;
                // const zIndex = baseZIndex + index * 2;
                // 내부 컴포넌트 생성
                const innerComponent = h(popupItem.component, {
                    key: `inner-${popupItem.key}`,
                    closeKey: popupItem.key,
                    ...popupItem.props,
                });

                // PopupLayout으로 래핑
                return h(
                    PopupLayout,
                    {
                        key: popupItem.key,
                        closeKey: popupItem.key,
                        rest: popupItem.props?.rest ?? {},
                        ...popupItem.props,
                        // zIndex: zIndex,
                        onVnodeMounted: (vnode) => {
                            popupItem.componentInstance = vnode.component;
                        },
                        onVnodeBeforeUnmount: () => {
                            popupItem.componentInstance = null;
                        },
                    },
                    () => innerComponent,
                );
            });

            return h("div", { class: "w-full pointer-events-none" }, children);
        };
    },
});

export interface PopupManager {
    openPopup: typeof openPopup;
    closePopup: typeof closePopup;
    clearPopup: typeof clearPopup;
    getPopupList: typeof getPopupList;
}

/**
 * @description 팝업 관리자 plugin
 * @param {App} app
 */
const popupManagerPlugin = {
    install: (app: App) => {
        // 전역 프로퍼티를 등록합니다.
        app.config.globalProperties.$popupManager = {
            openPopup,
            closePopup,
            clearPopup,
            getPopupList,
        };

        // 팝업 앵커 컴포넌트를 등록합니다.
        app.component("PopupAnchor", PopupAnchor);
    },
};

export default popupManagerPlugin;
